import {
  Alert,
  Button,
  Checkbox,
  Group,
  Modal,
  Paper,
  Progress,
  SimpleGrid,
  Stack,
  Text,
  TextInput,
} from '@mantine/core';
import { notifications } from '@mantine/notifications';
import { IconCheck, IconCloudUpload, IconDownload, IconInfoCircle } from '@tabler/icons-react';
import React, { useState } from 'react';

import { uploadFile } from '../../../services/upload';
import { generateLaserDxf } from '../engine/exporters/dxf-exporter';
import { generateLaserSvg } from '../engine/exporters/svg-exporter';
import { useVectorCadStore } from '../stores/vector-cad-store';

interface ExportModalProps {
  opened: boolean;
  onClose: () => void;
}

export function ExportModal({ opened, onClose }: ExportModalProps) {
  const { geometryResult, weldScoreConfig } = useVectorCadStore();

  const [filename, setFilename] = useState('laser-quote-design');
  const [includeCut, setIncludeCut] = useState(true);
  const [includeScore, setIncludeScore] = useState(true);
  const [includeBacker, setIncludeBacker] = useState(true);
  const [includeGuides, setIncludeGuides] = useState(true);

  const [isSavingToDrive, setIsSavingToDrive] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);

  if (!geometryResult) return null;

  const handleDownloadSvg = () => {
    const svgContent = generateLaserSvg({
      geometry: geometryResult,
      weldScoreConfig,
      includeCut,
      includeScore,
      includeBacker,
      includeGuides,
    });

    const blob = new Blob([svgContent], { type: 'image/svg+xml;charset=utf-8' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `${filename || 'laser-quote-design'}.svg`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);

    notifications.show({
      title: 'SVG Exported',
      message: `${filename}.svg is ready for laser cutting`,
      color: 'teal',
      icon: <IconCheck size={16} />,
    });
  };

  const handleDownloadDxf = () => {
    const dxfContent = generateLaserDxf({
      geometry: geometryResult,
      includeCut,
      includeScore,
      includeBacker,
    });

    const blob = new Blob([dxfContent], { type: 'application/dxf;charset=utf-8' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `${filename || 'laser-quote-design'}.dxf`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);

    notifications.show({
      title: 'DXF Exported',
      message: `${filename}.dxf is ready for CAD/CNC`,
      color: 'blue',
      icon: <IconCheck size={16} />,
    });
  };

  const handleSaveToDrive = async () => {
    setIsSavingToDrive(true);
    setUploadProgress(10);

    try {
      const svgContent = generateLaserSvg({
        geometry: geometryResult,
        weldScoreConfig,
        includeCut,
        includeScore,
        includeBacker,
        includeGuides,
      });

      const file = new File([svgContent], `${filename || 'laser-quote-design'}.svg`, {
        type: 'image/svg+xml',
      });

      const abortController = new AbortController();
      await uploadFile(
        file,
        (progress) => setUploadProgress(progress),
        abortController.signal,
        null, // root folder
      );

      notifications.show({
        title: 'Saved to Company Drive',
        message: `Successfully uploaded ${file.name} to Disit Drive!`,
        color: 'teal',
        icon: <IconCheck size={16} />,
      });
      onClose();
    } catch (err: any) {
      notifications.show({
        title: 'Save to Drive Failed',
        message: err.message || 'Could not upload design to Drive',
        color: 'red',
      });
    } finally {
      setIsSavingToDrive(false);
      setUploadProgress(0);
    }
  };

  return (
    <Modal opened={opened} onClose={onClose} title='Export Laser CAD Files' size='lg' centered>
      <Stack gap='md'>
        {/* Filename Input */}
        <TextInput
          label='File Name'
          placeholder='e.g. forever-always-quote'
          value={filename}
          onChange={(e) => setFilename(e.target.value)}
        />

        {/* Layer Inclusion Checkboxes */}
        <Paper withBorder p='sm' radius='md'>
          <Stack gap='xs'>
            <Text size='xs' fw={600} tt='uppercase' c='dimmed'>
              Include Vector Layers
            </Text>
            <SimpleGrid cols={2}>
              <Checkbox
                label='Cut Outline (Red #FF0000)'
                checked={includeCut}
                onChange={(e) => setIncludeCut(e.currentTarget.checked)}
                color='red'
              />
              <Checkbox
                label='Inner Score Lines (Blue #0055FF)'
                checked={includeScore}
                onChange={(e) => setIncludeScore(e.currentTarget.checked)}
                color='blue'
              />
              <Checkbox
                label='Offset Backer (Green #00CC00)'
                checked={includeBacker}
                onChange={(e) => setIncludeBacker(e.currentTarget.checked)}
                color='green'
              />
              <Checkbox
                label='Alignment Guides (Cyan #00CCCC)'
                checked={includeGuides}
                onChange={(e) => setIncludeGuides(e.currentTarget.checked)}
                color='cyan'
              />
            </SimpleGrid>
          </Stack>
        </Paper>

        {/* Compatibility Info */}
        <Alert icon={<IconInfoCircle size={16} />} color='gray' variant='light' p='xs'>
          <Text size='xs'>
            Standard laser color separation: <b>Red</b> is set to Cut, <b>Blue</b> is set to Score
            (low power, high speed). Compatible with{' '}
            <b>LightBurn, Glowforge, xTool, RDWorks, K40, Cricut, Inkscape, and AutoCAD</b>.
          </Text>
        </Alert>

        {isSavingToDrive && <Progress value={uploadProgress} size='sm' striped animated />}

        {/* Export Buttons */}
        <Group justify='space-between' mt='sm'>
          <Button
            variant='gradient'
            gradient={{ from: 'teal', to: 'lime' }}
            leftSection={<IconCloudUpload size={16} />}
            loading={isSavingToDrive}
            onClick={handleSaveToDrive}
          >
            Save Directly to Drive
          </Button>

          <Group gap='xs'>
            <Button
              variant='light'
              color='blue'
              leftSection={<IconDownload size={16} />}
              onClick={handleDownloadDxf}
            >
              Download DXF
            </Button>
            <Button
              variant='filled'
              color='blue'
              leftSection={<IconDownload size={16} />}
              onClick={handleDownloadSvg}
            >
              Download SVG
            </Button>
          </Group>
        </Group>
      </Stack>
    </Modal>
  );
}
